import React from 'react';
import Select from '@amzn/awsui-components-react-v3/polaris/select';
import Badge from '@amzn/awsui-components-react-v3/polaris/badge';

const MappingStatus = {
  ONGOING: 'Ongoing',
  SCHEDULED: 'Scheduled',
  HISTORICAL: 'Historical',
};

/**
 * Determines if a mapping is ongoing (currently active with no end date).
 * A mapping is considered ongoing if its endMonth is null or undefined.
 *
 * @param {Object} mapping - The mapping object to check
 * @returns {boolean} True if the mapping is ongoing, false otherwise
 */
export function isLatest(mapping) {
  return mapping && mapping.endMonth === null;
}

/**
 * Determines the status of a mapping relative to the current month.
 * - "Ongoing": current month falls within the mapping's time range
 * - "Scheduled": mapping starts in the future
 * - "Historical": mapping ended in the past
 *
 * @param {Object} mapping - The mapping object with startMonth and endMonth
 * @returns {string} One of "Ongoing", "Scheduled", or "Historical"
 */
export function getMappingStatus(mapping) {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  const { startMonth, endMonth } = mapping;

  // If startMonth is in the future, it's scheduled
  if (startMonth && startMonth > currentMonth) {
    return MappingStatus.SCHEDULED;
  }

  // If endMonth is in the past, it's historical
  if (endMonth && endMonth < currentMonth) {
    return MappingStatus.HISTORICAL;
  }

  // Otherwise, current month is within range
  return MappingStatus.ONGOING;
}

/**
 * Formats a YYYYMM string to "Mon YYYY" format (e.g., "202502" -> "Feb 2025").
 *
 * @param {string} yyyymmDateString - Month in YYYYMM format
 * @returns {string} Formatted month string like "Feb 2025", or "*" if invalid
 */
export function formatMonth(yyyymmDateString) {
  const year = yyyymmDateString.substring(0, 4);
  const monthIndex = parseInt(yyyymmDateString.substring(4, 6), 10) - 1;

  if (monthIndex < 0 || monthIndex > 11) {
    return yyyymmDateString; // Return original if invalid month - shouldn't happen
  }

  const date = new Date(parseInt(year), monthIndex);
  return `${date.toLocaleString('en-US', { month: 'short' })} ${year}`;
}

/**
 * Formats a time range for display in the TimePeriodSelector.
 * - If startMonth is null: "till Jan 2025"
 * - If endMonth is null: "Feb 2025 onwards"
 * - If both present: "Jan 2025 - Feb 2025"
 *
 * @param {string|null} startMonth - Start month in YYYYMM format or null
 * @param {string|null} endMonth - End month in YYYYMM format or null
 * @returns {string} Formatted time range string
 */
export function formatTimeRange(startMonth, endMonth) {
  if (!startMonth && endMonth) {
    return `Till ${formatMonth(endMonth)}`;
  }
  if (startMonth && !endMonth) {
    return `${formatMonth(startMonth)} onwards`;
  }
  if (startMonth && endMonth) {
    return `${formatMonth(startMonth)} - ${formatMonth(endMonth)}`;
  }
  // Both null - shouldn't happen
  return 'All time';
}

/**
 * Sorts mappings with ongoing mappings first, then historical mappings by endMonth descending.
 * This ensures the most recent/relevant mappings appear at the top of the list.
 *
 * @param {Array} mappings - Array of mapping objects with startMonth and endMonth fields
 * @returns {Array} Sorted array of mappings
 */
export function sortMappings(mappings) {
  if (!mappings || !Array.isArray(mappings)) {
    return [];
  }

  return [...mappings].sort((a, b) => {
    const aLatest = isLatest(a);
    const bLatest = isLatest(b);

    // Latest mapping come first
    if (aLatest && !bLatest) {
      return -1;
    }
    if (!aLatest && bLatest) {
      return 1;
    }

    // For latest mappings (both null), they're equal - shouldn't happen
    if (aLatest && bLatest) {
      return 0;
    }

    // Both are historical - sort by endMonth descending (most recent first)
    const aEnd = a.endMonth || '';
    const bEnd = b.endMonth || '';
    return bEnd.localeCompare(aEnd);
  });
}

/**
 * Generates a unique time key for a mapping based on its time boundaries.
 * Format: "startMonth-endMonth" where null values become "*".
 *
 * @param {Object} mapping - The mapping object
 * @returns {string} Time key in format "startMonth-endMonth"
 */
export function getTimeKey(mapping) {
  const startMonth = mapping && mapping.startMonth;
  const endMonth = mapping && mapping.endMonth;
  const start = startMonth === null ? '*' : startMonth;
  const end = endMonth === null ? '*' : endMonth;
  return start + '-' + end;
}

/**
 * Computes dropdown options with badge rendering for the TimePeriodSelector.
 * Each option includes a label with an inline Badge component for visual status indication.
 * Options are sorted with ongoing mappings first, then by endMonth descending.
 *
 * Badge colors:
 * - Green for Ongoing mappings (current month is within range)
 * - Blue for Scheduled mappings (start month is in the future)
 * - Grey for Historical mappings (end month is in the past)
 *
 * @param {Array} mappings - Array of mapping objects with startMonth and endMonth fields
 * @returns {Array} Array of option objects for Polaris v3 Select (label, value)
 */
export function computeOptionsWithBadges(mappings) {
  if (!mappings || !Array.isArray(mappings) || mappings.length === 0) {
    return [];
  }

  const sortedMappings = sortMappings(mappings);
  const badgeColors = {
    [MappingStatus.ONGOING]: 'green',
    [MappingStatus.SCHEDULED]: 'blue',
    [MappingStatus.HISTORICAL]: 'grey'
  };

  return sortedMappings.map((mapping) => {
    const timeKey = getTimeKey(mapping);
    const timeRangeLabel = formatTimeRange(mapping.startMonth, mapping.endMonth);
    const status = getMappingStatus(mapping);

    // Polaris v3 Select:
    // - 'label' is shown in the dropdown options (with badge)
    // - 'selectedLabel' is shown when the option is selected (without badge)
    return {
      label: (
        <span>
          <Badge color={badgeColors[status]}>{status}</Badge>
          {' '}
          {timeRangeLabel}
        </span>
      ),
      selectedLabel: timeRangeLabel,
      value: timeKey
    };
  });
}

/**
 * TimePeriodSelector - A reusable dropdown component for selecting which time period's mapping to view.
 * Used in both Component Section and Sub Components Section of the MappingTable.
 *
 * Displays mappings sorted with ongoing first, then historical by endMonth descending.
 * Each option shows a description: "Ongoing" or "Historical".
 *
 * @param {Object} props
 * @param {Array} props.mappings - Array of mapping objects with startMonth and endMonth fields
 * @param {string} props.selectedTimeKey - Currently selected time key in format "startMonth-endMonth"
 * @param {Function} props.onChange - Callback function when selection changes, receives the new timeKey
 * @param {boolean} props.disabled - Whether the selector is disabled
 */
export class TimePeriodSelector extends React.Component {
  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
  }

  /**
   * Handles selection change from the Polaris v3 Select component.
   * Extracts the selected timeKey (value) and passes it to the parent onChange callback.
   *
   * @param {Object} event - The change event from Polaris v3 Select
   */
  handleChange(event) {
    const { onChange } = this.props;
    if (onChange && event.detail.selectedOption) {
      const selectedValue = event.detail.selectedOption.value;
      onChange(selectedValue);
    }
  }

  render() {
    const { mappings, selectedTimeKey, disabled } = this.props;

    // Compute options from mappings
    const options = computeOptionsWithBadges(mappings);

    // If no mappings, don't render the selector
    if (!options || options.length === 0) {
      return null;
    }

    // Find the currently selected option object for Select
    // For the trigger display, we create a modified selectedOption with just the text label without the badge
    const selectedOptionFromList = options.find(option => option.value === selectedTimeKey);
    
    // Create a selectedOption with plain text label for the trigger display
    // The dropdown will still show badges because it uses the options array
    let selectedOption = null;
    if (selectedOptionFromList) {
      // Find the original mapping to get the plain text label
      const sortedMappings = sortMappings(mappings);
      const selectedMapping = sortedMappings.find(mapping => getTimeKey(mapping) === selectedTimeKey);
      if (selectedMapping) {
        selectedOption = {
          label: formatTimeRange(selectedMapping.startMonth, selectedMapping.endMonth),
          value: selectedTimeKey
        };
      }
    }

    return (
      <Select
        selectedOption={selectedOption}
        onChange={this.handleChange}
        disabled={disabled}
        options={options}
        placeholder="Select time period"
      />
    );
  }
}

export default TimePeriodSelector;



// WEBPACK FOOTER //
// ./src/components/mapper/time_period_selector.jsx